/**
 * League XP preview math — mirrors backend finalize in `leaguesLifecycle` /
 * `leagueXpMath.js`. Kept separate from `leagueFormat` to avoid a cycle with
 * `leaguePlays` (settled points).
 */

import {
    LEAGUE_GAME_KEYS,
    LEAGUE_GAME_META,
    LEAGUE_XP_GAME_WIN,
    LEAGUE_XP_PLACEMENT_BONUS,
    LEAGUE_XP_POINTS_CAP,
    LEAGUE_XP_POINTS_PCT,
} from "./constants";
import { isLeagueXpLive, leagueStatus, leagueTableRows } from "./leagueFormat";
import { leaguePlaySettledPoints } from "./leaguePlays";
import type { League, LeagueGameKey, LeagueTableRow } from "./types";

export type LeagueXpBreakdownPart = {
    kind: "points" | "placement" | "game_win";
    xp: number;
    /** Short label for ended-footer / share copy. */
    label: string;
    game?: LeagueGameKey;
    place?: number;
    points?: number;
    capped?: boolean;
};

export type LeagueXpBreakdown = {
    total: number;
    parts: LeagueXpBreakdownPart[];
};

function leagueScoreXpFromPoints(points: number): { xp: number; raw: number; capped: boolean; points: number } {
    const pts = Math.max(0, Math.floor(Number(points) || 0));
    const raw = Math.floor(pts * (LEAGUE_XP_POINTS_PCT / 100));
    const xp = Math.min(raw, LEAGUE_XP_POINTS_CAP);
    return { xp, raw, capped: xp < raw, points: pts };
}

/** Eligible standings order: ≥1 settled play (same gate as prize / XP finalize). */
function leagueXpEligibleRows(league: League): LeagueTableRow[] {
    const settledUsers = new Set<number>();
    for (const play of league.plays) {
        if (play.status === "settled") settledUsers.add(play.userId);
    }
    return leagueTableRows(league).filter((entry) => settledUsers.has(entry.userId));
}

/**
 * Game types this user tops among eligible members (settled points only; ties share).
 * Mirrors backend `leagueGameWinnersByGame`.
 */
export function leagueGameWinsForRow(league: League, userId: number): LeagueGameKey[] {
    const eligible = new Set(leagueXpEligibleRows(league).map((row) => row.userId));
    if (!eligible.has(userId)) return [];

    const byGame = new Map<LeagueGameKey, Map<number, number>>();
    for (const play of league.plays) {
        if (play.status !== "settled" || !eligible.has(play.userId)) continue;
        const pts = leaguePlaySettledPoints(play);
        if (pts <= 0) continue;
        let userMap = byGame.get(play.game);
        if (!userMap) {
            userMap = new Map();
            byGame.set(play.game, userMap);
        }
        userMap.set(play.userId, (userMap.get(play.userId) ?? 0) + pts);
    }

    const wins: LeagueGameKey[] = [];
    for (const game of LEAGUE_GAME_KEYS) {
        const userMap = byGame.get(game);
        if (!userMap) continue;
        let best = 0;
        for (const pts of userMap.values()) {
            if (pts > best) best = pts;
        }
        if (best <= 0) continue;
        if ((userMap.get(userId) ?? 0) === best) wins.push(game);
    }
    return wins;
}

/**
 * XP breakdown for a standings row. Score XP tracks live points once the league
 * has started; game-win and podium bonuses apply when those results are known
 * (settled game tops / ended podium). Idle seats (no settled plays) get 0.
 */
export function leagueXpBreakdownForRow(
    league: League,
    row: LeagueTableRow,
    now: number = Date.now(),
): LeagueXpBreakdown {
    if (!isLeagueXpLive(league, now)) return { total: 0, parts: [] };
    const settledCount = league.plays.filter((p) => p.userId === row.userId && p.status === "settled").length;
    if (settledCount <= 0) return { total: 0, parts: [] };

    const parts: LeagueXpBreakdownPart[] = [];
    const score = leagueScoreXpFromPoints(row.wonPoints);
    if (score.xp > 0) {
        parts.push({
            kind: "points",
            xp: score.xp,
            points: score.points,
            capped: score.capped,
            label: `${score.xp} XP from ${score.points.toLocaleString("en-US")} pts from League play`,
        });
    }

    for (const game of leagueGameWinsForRow(league, row.userId)) {
        if (LEAGUE_XP_GAME_WIN <= 0) break;
        const meta = LEAGUE_GAME_META[game];
        parts.push({
            kind: "game_win",
            xp: LEAGUE_XP_GAME_WIN,
            game,
            label: `${LEAGUE_XP_GAME_WIN} ${meta?.label ?? game} win`,
        });
    }

    if (leagueStatus(league, now) === "ended") {
        const eligible = leagueXpEligibleRows(league);
        const podiumIndex = eligible.findIndex((entry) => entry.userId === row.userId);
        if (podiumIndex >= 0 && podiumIndex < LEAGUE_XP_PLACEMENT_BONUS.length) {
            const bonus = LEAGUE_XP_PLACEMENT_BONUS[podiumIndex] ?? 0;
            if (bonus > 0) {
                const place = podiumIndex + 1;
                const placeLabel = place === 1 ? "1st" : place === 2 ? "2nd" : place === 3 ? "3rd" : `${place}th`;
                parts.push({
                    kind: "placement",
                    xp: bonus,
                    place,
                    label: `${bonus} for ${placeLabel}`,
                });
            }
        }
    }

    return {
        total: parts.reduce((sum, part) => sum + part.xp, 0),
        parts,
    };
}

/** One-line XP reason string for the ended footer. */
export function formatLeagueXpBreakdownLine(breakdown: LeagueXpBreakdown): string {
    return breakdown.parts.map((part) => part.label).join(" · ");
}

/**
 * Total XP a row has earned (or will earn at settle). Matches finalize grants in
 * `leaguesLifecycle`.
 */
export function leagueXpForRow(league: League, row: LeagueTableRow, now: number = Date.now()): number {
    return leagueXpBreakdownForRow(league, row, now).total;
}
